import { DashboardCard, brl, fact, plural, strong } from '../../shared/dashboard/dashboard.model';
import { Month, shiftMonth } from '../../shared/month/month.service';
import { FinanceRecords, MonthSummary, round2, sum, summarizeFinanceMonth } from '../finance-summary.service';

export const HISTORY_MONTHS = 6;

export interface FinanceDashboard {
  summary: MonthSummary,
  cards: DashboardCard[],
  history: { month: Month, incomes: number, spent: number }[]
}

export function buildFinanceDashboard(records: FinanceRecords, month: Month): FinanceDashboard {
  const months = Array.from({ length: HISTORY_MONTHS }, (_, i) => shiftMonth(month, i - HISTORY_MONTHS + 1));
  const summaries = months.map(m => summarizeFinanceMonth(records, m));
  const summary = summaries[summaries.length - 1];
  const previous = summaries[summaries.length - 2];

  return {
    summary,
    history: summaries.map((s, i) => ({ month: months[i], incomes: s.totals.incomes, spent: round2(s.totals.bills + s.totals.expenses) })),
    cards: [
      incomesCard(summary, summaries.map(s => s.totals.incomes)),
      billsCard(summary, previous, summaries.map(s => s.totals.bills)),
      expensesCard(summary, summaries.map(s => s.totals.expenses)),
      cardsCard(summary, records, month, summaries.map(s => sum(s.cards)))
    ]
  };
}

function largest<T extends { value: number }>(items: T[]): T | undefined {
  return [...items].sort((a, b) => b.value - a.value)[0];
}

function incomesCard(summary: MonthSummary, series: number[]): DashboardCard {
  const lines = [...summary.incomes.map(i => ({ description: i.description, value: Number(i.value) || 0 })),
    ...(summary.dividends ? [{ description: 'Dividendos', value: summary.dividends }] : [])];
  const top = largest(lines);
  return {
    icon: 'trending_up', title: 'Incomes', link: '/finances/incomes', linkLabel: 'Abrir Incomes',
    chip: lines.length ? { text: plural(lines.length, 'lançamento', 'lançamentos'), tone: 'info' } : null,
    figure: brl(summary.totals.incomes), series,
    facts: [
      top ? fact('Maior: ', strong(top.description), ` ${brl(top.value)}`) : fact('Nenhuma receita neste mês'),
      ...(summary.dividends ? [fact(`Inclui ${brl(summary.dividends)} de dividendos de Investments`)] : [])
    ]
  };
}

// Bills repeat every month: the ones of the previous month not launched yet are flagged
function billsCard(summary: MonthSummary, previous: MonthSummary, series: number[]): DashboardCard {
  const current = new Set(summary.bills.map(b => b.description.toLowerCase()));
  const missing = previous.bills.filter(b => !current.has(b.description.toLowerCase())).map(b => b.description);
  const top = largest(summary.bills);
  return {
    icon: 'receipt_long', title: 'Bills', link: '/finances/bills', linkLabel: 'Abrir Bills',
    chip: missing.length ? { text: `${missing.length} faltando`, tone: 'warn' } : null,
    figure: brl(summary.totals.bills), figureNote: plural(summary.bills.length, 'conta', 'contas'), series,
    facts: [
      top ? fact('Maior: ', strong(top.description), ` ${brl(top.value)}`) : fact('Nenhuma conta neste mês'),
      ...(missing.length ? [fact('Ainda não lançadas: ', strong(missing.slice(0, 2).join(', ')), missing.length > 2 ? ` e mais ${missing.length - 2}` : '')] : [])
    ]
  };
}

function expensesCard(summary: MonthSummary, series: number[]): DashboardCard {
  const cards = sum(summary.cards);
  const others = round2(summary.totals.expenses - cards);
  const top = largest(summary.expenses);
  return {
    icon: 'shopping_bag', title: 'Expenses', link: '/finances/expenses', linkLabel: 'Abrir Expenses',
    chip: summary.expenses.length ? { text: plural(summary.expenses.length, 'saída', 'saídas'), tone: 'info' } : null,
    figure: brl(summary.totals.expenses), series,
    facts: [
      fact('Cartões ', strong(brl(cards)), ` · outras ${brl(others)}`),
      top ? fact('Maior fora do cartão: ', strong(top.description), ` ${brl(top.value)}`) : fact('Nenhuma saída fora do cartão')
    ]
  };
}

function cardsCard(summary: MonthSummary, records: FinanceRecords, month: Month, series: number[]): DashboardCard {
  const uploaded = new Set(records.statements.filter(s => s.month == month).map(s => s.card));
  const withoutStatement = records.cards.filter(c => c.active && !uploaded.has(c.id));
  const total = sum(summary.cards);
  return {
    icon: 'credit_card', title: 'Credit Card', link: '/finances/credit-card', linkLabel: 'Abrir Credit Card',
    chip: withoutStatement.length
      ? { text: `${withoutStatement.length} sem fatura`, tone: 'warn' }
      : uploaded.size ? { text: `${plural(uploaded.size, 'fatura enviada', 'faturas enviadas')}`, tone: 'ok' } : null,
    figure: brl(total), figureNote: plural(summary.statements.length, 'cartão', 'cartões'), series,
    facts: summary.statements.length
      ? summary.statements.slice(0, 2).map(s => fact(strong(s.card.name), ` ${brl(s.total)}`, s.card.dueDay ? ` · vence dia ${s.card.dueDay}` : ''))
      : [fact(withoutStatement.length ? `Falta a fatura de ${withoutStatement.map(c => c.name).join(', ')}` : 'Nenhum item de cartão neste mês')]
  };
}
